// websocket/walk-trap-truth.ts — Janus session 3 (Movement) step 4, 2026-09-29: the page half of a WALK's trap fire.
//
// The legacy walk loop ran ft_trap_check_step_triggers() before every foot and fired an armed host with the SERVER's dice (ft_trap_fire —
// R-102's last caller). A client-truth walk (movement-truth.ts) decides it here before committing, with resumable player consent:
//   · the READERS the trigger twin (janus-engine/movement/walk-trap.ts) needs — the page's stored status rows (a participant's cache keeps the
//     private armed row), the host's mounted payloads (trap-truth.ts mountedTrapAbilityIds), and PHP's centred occupancy shapes for the host
//     (the inventory footprint twin: a native mesh oblong, else the size-tier cylinder) and the mover;
//   · the FIRE — TRAPS' shared capability preflight and actual-outcome resolver, including the typed death plan. The verdict
//     rides the walk commit (movement_solid_truth_inc.php `trap`), which records it through the shared recorder in the walk's transaction.
// Registered with movement-truth.ts (registerWalkTrapPlanner) so this module can use the ability lane's readers without an import cycle.
import { preflightTrapFire, resolveTrapFire, type TrapFireInput, type TrapFireVerdict } from '../janus-engine/traps/fire.js';
import { normalizePhpListish } from '../shared/php-listish.js';
import { TRIGGER_CONSENT_REASON } from '../janus-engine/ability/trigger-consent.js';
import { browserRollSource, resumableRollSource } from '../janus-engine/actions/roll-source.js';
import { prepareDeathResources } from '../shared/spawn-browser.js';
import { planWithConsent } from './trigger-consent.js';
import { applyMovementTruth, type MovementTruthRow } from '../janus-engine/movement/client-truth-commit.js';
import { ecLean } from '../janus-engine/ability/context.js';
import { physicalMeshBoundsFt, type Zone } from '../janus-engine/inventory/floor-move.js';
import { resolveWalkTrap, type WalkTrapReaders } from '../janus-engine/movement/walk-trap.js';
import { legacyStepPositions } from '../janus-engine/zones/walk-zone-crossings.js';
import { abilityTruthReaders, hydrateTurnTriggerDefinitions } from './ability-truth.js';
import { footprintInputs, sizeBase } from './inventory-floor-move-truth.js';
import { actorEncounterFor, actorRowFor, movementScopeReaders, registerWalkTrapPlanner, type WalkTrapPlanner } from './movement-truth.js';
import { mountedTrapAbilityIds } from './trap-truth.js';
import { trapClockMaintenance } from '../janus-engine/traps/disarm-begin.js';
import { withTrapClockMaintenance } from '../janus-engine/traps/clock-maintenance.js';
import { fetchAndCacheObject } from './object-cache.js';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(Array.isArray(v) ? v[0] : v ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
/** The fixed statuses a fire's cascade reads (Fortunate, Prone, Dying, Crippled, Maimed) — trap-truth.ts buildFire's list. */
const FIXED = ['AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8', 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA', '8AE129AF-52D8-CCE8-706A-85767DB52740',
  '99C862B9-D6AE-4E18-7803-A6F9B42A88E1', 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D'];
const layerEntries = (row: Row, key: string): unknown[] => { try { const e = ftLayerEntries(row, key); return Array.isArray(e) ? e : []; } catch { return []; } };
const layerValue = (row: Row, key: string): unknown => { try { return ftLayerValue(row, key); } catch { return null; } };
const objectCache = (): Map<string, unknown> | undefined => (window as unknown as { FateTide?: { objectCache?: Map<string, unknown> } }).FateTide?.objectCache;
function definitionOf(encounter: Row, host: Row | null, id: string): Row | null {
  const key = upper(id);
  for (const c of [asRow(host?.catalog), asRow(encounter.catalog)]) { const d = asRow(c?.[key]); if (d && asRow(d.items)) return d; }
  const cached = asRow(objectCache()?.get(key));
  return cached && asRow(cached.items) ? cached : null;
}
const warmed = new Set<string>();
function warm(ids: string[]): void { for (const id of ids) if (!warmed.has(id)) { warmed.add(id); void fetchAndCacheObject(id).catch(() => { warmed.delete(id); }); } }
const position = (row: Row): [number, number, number] => {
  const p = asRow(row.position) ?? {};
  const n = (v: unknown): number => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
  return [n(p.x_pos), n(p.y_pos), n(p.z_pos)];
};

/** ft_ground_occupancy_shape($host) — NOT the support plane: a native mesh (container / furniture / statue …) is an oblong of its measured
 *  bounds, else the size-tier cylinder; both spans centred on the row's z (the per-foot loop's call). */
function hostZone(encounter: Row, host: Row): Zone | null {
  try {
    const input = footprintInputs(encounter, host);
    const mesh = physicalMeshBoundsFt(input, sizeBase);
    const facing = -((input.transform?.rotationDegrees ?? 0) * Math.PI / 180);
    if (mesh) return { kind: 'oblong', x: input.x, y: input.y, halfW: mesh[0] / 2, halfD: mesh[2] / 2, facing, zMin: input.z - mesh[1] / 2, zMax: input.z + mesh[1] / 2 } as Zone;
    // a scaled (non-mesh) transform is not modelled by the centred reader
    if (input.transform?.scaleXYZ || input.transform?.scale) return null;
    const base = (input.sizeId ? sizeBase(input.sizeId) : null) ?? 5;
    return { kind: 'circle', x: input.x, y: input.y, r: base / 2, zMin: input.z - base / 2, zMax: input.z + base / 2 } as Zone;
  } catch { return null; }
}

export function walkTrapReaders(encounter: Row, mover: Row): WalkTrapReaders {
  const scope = movementScopeReaders(encounter);
  const body = scope.geometry(mover);
  const [, , mz] = scope.position(mover);
  return {
    statuses: row => ftCreatureStatuses(row, 'target') as Row[],
    trappedId: typeof VISUAL_STATUS_EFFECTS === 'undefined' ? '' : String(VISUAL_STATUS_EFFECTS.trapped ?? ''),
    mountedIds: host => mountedTrapAbilityIds(host),
    definition: id => { const d = definitionOf(encounter, null, id); if (!d) warm([upper(id)]); return d; },
    entries: (def, key) => layerEntries(def, key),
    value: (def, key) => layerValue(def, key),
    hostXY: host => { const [x, y] = position(host); return [x, y]; },
    hostZone: host => hostZone(encounter, host),
    // the mover's centred body (a mesh body's width, else the size tier) — a swarm's widened frontage is not modelled
    moverZoneAt: (x, y) => body && body.diameter > 0 && body.height > 0
      ? { kind: 'circle', x, y, r: body.diameter / 2, zMin: mz - body.height / 2, zMax: mz + body.height / 2 } as Zone : null,
  };
}

type FireResult = Awaited<ReturnType<WalkTrapPlanner['plan']>>;
function prepareFireInput(encounterId: string, victimId: string, hostId: string, abilityId: string,
  seed?: Parameters<WalkTrapPlanner['plan']>[4]): TrapFireInput | Extract<FireResult, { kind: 'handback' }> {
    const encounter = actorEncounterFor(encounterId) as Row | null;
    const heldVictim = actorRowFor(encounterId, victimId) as unknown as Row | null;
    if (!encounter || !heldVictim) return { kind: 'handback', reason: 'walk-trap:not-held' };
    if (!asRow(asRow(heldVictim.computed)?.creature) || !asRow(heldVictim.item_layer_types)) return { kind: 'handback', reason: 'walk-trap:victim-compile-not-held' };
    const host = (Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : [])
      .find(r => upper(asRow(r.inventory)?.inventory_id) === upper(hostId)) ?? null;
    if (!host) return { kind: 'handback', reason: 'walk-trap:host-not-held' };
    const def = definitionOf(encounter, host, abilityId);
    if (!def) { warm([upper(abilityId)]); return { kind: 'handback', reason: 'walk-trap:definition-not-held' }; }
    // every definition the payload reads, held BEFORE resolving (trap-truth.ts buildFire's list)
    const needed = new Set<string>(FIXED);
    for (const key of ['damage_type', 'skill_roll_contestee', 'status_effect_applied_to_target_after_contesting_roll_failed'])
      for (const e of layerEntries(def, key)) {
        const id = upper(typeof e === 'string' ? e.split(/[\s|]/)[0] : asRow(e)?.item_id ?? asRow(asRow(e)?.items)?.item_id);
        if (UUID.test(id)) needed.add(id);
      }
    const missing = [...needed].filter(id => !definitionOf(encounter, host, id));
    if (missing.length) { warm(missing); return { kind: 'handback', reason: 'walk-trap:definition-not-held' }; }
    // MV10 (2026-10-08): the stealth recorder runs first; trap luck CAS starts at its after-value, including the reader's victim row.
    // Only a seeded row is copied (a shallow copy with its own encounter_creatures) — the held cache is never mutated.
    const luckSeed = new Map<string, unknown>();
    for (const [id, cols] of Object.entries(seed ?? {})) if (cols.luck !== undefined) luckSeed.set(upper(id), cols.luck);
    const seeded = (r: Row, id: string): Row => luckSeed.has(id) ? { ...r, encounter_creatures: { ...(asRow(r.encounter_creatures) ?? {}), luck: luckSeed.get(id) } } : r;
    const victim = seeded(heldVictim, upper(victimId));
    const members = new Map<string, Row>();
    for (const entry of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
      const r = asRow(entry), id = upper(asRow(r?.encounter_creatures)?.encounter_creature_id);
      if (r && id) members.set(id, seeded(r, id));
    }
    return { encounter, members, host, hostId: upper(hostId), victimId: upper(victimId), abilityId: upper(abilityId),
      read: abilityTruthReaders(victim, encounter, new Set([upper(victimId)])), rollPrivate: 0 };

}
function fireResult(verdict: TrapFireVerdict, abilityId: string, victimId: string): FireResult {
  if (verdict.kind === 'no_fire') return { kind: 'fire', fire: { mode: 'no_fire' } };
  if (verdict.kind === 'unmodelled') return { kind: 'handback', reason: `walk-trap:real-draw-residual:${verdict.reason}` };
  return { kind: 'fire', fire: { mode: 'rolled-v2', ability_id: upper(abilityId), victim_id: upper(victimId), verdict: {
    rows: verdict.rows, ec: verdict.ec, ecBefore: verdict.ecBefore, statusesAdded: verdict.statusesAdded,
    statusesRemoved: verdict.statusesRemoved, statusStacks: verdict.statusStacks, ...(verdict.spawnRows ? { spawnRows: verdict.spawnRows } : {}),
    charges: verdict.charges, pc: verdict.pc, deathTail: verdict.deathTail, lifecycle: verdict.lifecycle } } };
}
const planner: WalkTrapPlanner = {
  readers: (encounter, mover) => walkTrapReaders(encounter, mover),
  preflight(encounterId, victimId, hostId, abilityId, seed) {
    const input = prepareFireInput(encounterId, victimId, hostId, abilityId, seed);
    if ('kind' in input) return input;
    const result = preflightTrapFire(input);
    return result.kind === 'unmodelled' ? { kind: 'handback', reason: `walk-trap:unmodelled:${result.reason}` }
      : { kind: 'fire', fire: result.kind === 'no_fire' ? { mode: 'no_fire' } : {} };
  },
  async plan(encounterId, victimId, hostId, abilityId, seed, at) {
    const input = prepareFireInput(encounterId, victimId, hostId, abilityId, seed);
    if ('kind' in input) return input;
    const clock = trapClockMaintenance(input.host, input.members.get(upper(victimId))!, input.read);
    // Source loading and every controller answer use the same held movement/trap before-values.
    const held = structuredClone({ encounter: input.encounter, members: input.members, host: input.host });
    const victim = held.members.get(upper(victimId))!;
    if (at) applyMovementTruth(victim as unknown as MovementTruthRow, { x_pos: at.x, y_pos: at.y, z_pos: at.z,
      rev: Number(ecLean(victim, 'movement_rev') ?? 0) });
    try {
      await hydrateTurnTriggerDefinitions(victim, held.encounter, [], [...held.members.values(), held.host]);
      const baseRead = abilityTruthReaders(victim, held.encounter, new Set([upper(victimId)]));
      const deathResources = await prepareDeathResources([...held.members.values()], held.encounter, baseRead);
      const rolls = resumableRollSource(browserRollSource());
      const verdict = await planWithConsent(encounterId, victimId, consent => resolveTrapFire({ ...input, ...held,
        read: { ...baseRead, deathResources, triggerConsent: consent } }, rolls.next()),
        result => result.kind === 'unmodelled' && result.reason.includes(TRIGGER_CONSENT_REASON));
      const result = fireResult(verdict, abilityId, victimId);
      if (result.kind === 'fire') {
        result.fire = withTrapClockMaintenance(result.fire, clock);
      }
      return result;
    } catch (error) {
      return { kind: 'handback', reason: `walk-trap:real-draw-exception:${String((error as Error)?.message ?? error).slice(0, 80)}` };
    }
  },
};
registerWalkTrapPlanner(planner);

// The synchronous planner needs the host's and victims' status/carrier definitions before a walk can draw.
const inFlight = new Map<string, Promise<unknown>>();
async function prefetchTrapDefinitions(encounter: Row | null): Promise<void> {
  // the status map global arrives with the page's later scripts; a sweep at bundle load waits for the next event
  if (!encounter || typeof VISUAL_STATUS_EFFECTS === 'undefined') return;
  const trappedId = upper(VISUAL_STATUS_EFFECTS.trapped ?? '');
  const payloads = new Set<string>();
  for (const r of Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : []) {
    const row = asRow(r);
    if (!row || !(ftCreatureStatuses(row, 'target') as Row[]).some(st => upper(st.status_item_id ?? asRow(st.items)?.item_id) === trappedId)) continue;
    for (const id of mountedTrapAbilityIds(row)) if (UUID.test(id)) payloads.add(id);
  }
  if (!payloads.size) return;
  const roster = normalizePhpListish(encounter.encounter_creatures_arr).map(asRow).filter((r): r is Row => !!r);
  const hosts = normalizePhpListish(encounter.encounter_inventories_arr).map(asRow).filter((r): r is Row => !!r);
  await hydrateTurnTriggerDefinitions(roster[0] ?? {}, encounter, [], [...roster, ...hosts]);
  const fetchAll = (ids: string[]): Promise<unknown> => Promise.all(ids.map(id => {
    if (definitionOf(encounter, null, id)) return null;
    let flight = inFlight.get(id);
    if (!flight) { flight = fetchAndCacheObject(id).catch(() => null).finally(() => inFlight.delete(id)); inFlight.set(id, flight); }
    return flight;
  }));
  await fetchAll([...payloads, ...FIXED]);
  const next: string[] = [];
  for (const id of payloads) {
    const def = definitionOf(encounter, null, id);
    if (def) for (const key of ['damage_type', 'skill_roll_contestee', 'status_effect_applied_to_target_after_contesting_roll_failed'])
      for (const e of layerEntries(def, key)) {
        const nid = upper(typeof e === 'string' ? e.split(/[\s|]/)[0] : asRow(e)?.item_id ?? asRow(asRow(e)?.items)?.item_id);
        if (UUID.test(nid)) next.push(nid);
      }
  }
  await fetchAll(next);
}
const sweep = (): void => { try { for (const encounter of window.FateTide?.encounterCache?.values() ?? []) void prefetchTrapDefinitions(asRow(encounter)); } catch { /* cache not ready */ } };
for (const name of ['ft:encounter-state-received', 'ft:encounter-installed', 'ft:encounter-rows-patched'])
  window.addEventListener(name, event => { const enc = asRow((event as CustomEvent).detail?.encounterArr); if (enc) void prefetchTrapDefinitions(enc); else sweep(); });
sweep();
setTimeout(sweep, 2000);
setTimeout(sweep, 8000);
Object.assign(window, { ftWalkTrapPrefetch: (encounterId: string) => prefetchTrapDefinitions(actorEncounterFor(upper(encounterId)) as Row | null) });
/** Diagnostics (smokes): the trigger twin's verdict for a route on the page's rows as they stand, and each trapped host's shape. Read-only. */
Object.assign(window, { ftWalkTrapProbe: (encounterId: string, actorId: string, route: number[][]) => {
  const encounter = actorEncounterFor(upper(encounterId)) as Row | null;
  const mover = actorRowFor(upper(encounterId), upper(actorId)) as unknown as Row | null;
  if (!encounter || !mover) return null;
  const inventories = Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : [];
  const read = walkTrapReaders(encounter, mover);
  return { verdict: resolveWalkTrap(inventories, legacyStepPositions(route), read),
    hosts: inventories.filter(r => read.statuses(r).some(st => upper(st.status_item_id) === upper(read.trappedId))).map(r => ({ id: upper(asRow(r.inventory)?.inventory_id), zone: read.hostZone(r) })) };
} });
